<template>
  <el-dialog
    :title="title"
    :visible.sync="localVisible"
    :width="width"
    :top="top"
    :modal="modal"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :show-close="showClose"
    :destroy-on-close="destroyOnClose"
    @close="handleClose"
  >
    <!-- 自定义内容区域 -->
    <slot name="content"></slot>

    <!-- 表单区域 -->
    <el-form
      v-if="fields.length > 0"
      ref="formRef"
      :model="innerFormData"
      :label-position="labelPosition"
      :rules="rules"
      :inline="columnCount > 1"
      :label-width="labelWidth"
    >
      <div :style="{ display: 'flex', 'flex-wrap': 'wrap' }">
        <template v-for="(field, index) in fields">
          <div
            :key="field.prop"
            :style="{
              width: `calc(${100 / columnCount}% - 10px)`,
              'margin-right': index % columnCount === columnCount - 1 ? '0' : '10px',
              'margin-bottom': '15px'
            }"
          >
            <el-form-item :label="field.label" :prop="field.prop" :hidden="field.hidden">
              <template v-if="$scopedSlots[field.prop]">
                <slot
                  :name="field.prop"
                  :field="field"
                  :value="innerFormData[field.prop]"
                  :updateValue="
                    newVal => {
                      innerFormData[field.prop] = newVal
                      syncToParent(field.prop, newVal)
                    }
                  "
                />
              </template>

              <template v-else>
                <!-- 特殊处理select -->
                <template v-if="field.type === 'select'">
                  <el-select
                    v-model="innerFormData[field.prop]"
                    v-bind="field.props || {}"
                    :disabled="isView || !field.editable"
                    @change="value => handleFieldChange(field.prop, value)"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="option in field.props?.options || []"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    />
                  </el-select>
                </template>

                <template v-else>
                  <component
                    :is="getComponentByType(field.type)"
                    v-model="innerFormData[field.prop]"
                    v-bind="getComponentProps(field)"
                    :disabled="isView || !field.editable"
                    @change="value => handleFieldChange(field.prop, value)"
                  />
                </template>
              </template>
            </el-form-item>
          </div>
        </template>
      </div>
    </el-form>

    <!-- 页脚按钮区域 -->
    <div slot="footer" class="dialog-footer">
      <slot name="footer">
        <el-button @click="handleCancel">{{ cancelButtonText }}</el-button>
        <el-button v-if="!isView" type="primary" @click="handleConfirm">
          {{ confirmButtonText }}
        </el-button>
      </slot>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-ui'

// 表单组件映射
const componentMap = {
  input: 'el-input',
  select: 'el-select',
  'select-option': 'el-option',
  date: 'el-date-picker',
  'date-range': 'el-date-picker',
  time: 'el-time-picker',
  checkbox: 'el-checkbox',
  radio: 'el-radio',
  'radio-group': 'el-radio-group',
  'checkbox-group': 'el-checkbox-group',
  textarea: 'el-input',
  number: 'el-input-number',
  switch: 'el-switch',
  slider: 'el-slider',
  upload: 'el-upload',
  color: 'el-color-picker',
  rate: 'el-rate'
}

export default {
  name: 'ProDialog',
  components: {},
  props: {
    // 对话框标题
    title: {
      type: String,
      default: '提示'
    },
    // 对话框可见性
    visible: {
      type: Boolean,
      default: false
    },
    // 对话框宽度
    width: {
      type: [String, Number],
      default: '50%'
    },
    // 对话框距离顶部的位置
    top: {
      type: String,
      default: '15vh'
    },
    // 是否显示遮罩
    modal: {
      type: Boolean,
      default: true
    },
    // 点击遮罩是否关闭对话框
    closeOnClickModal: {
      type: Boolean,
      default: false
    },
    // 按下ESC键是否关闭对话框
    closeOnPressEscape: {
      type: Boolean,
      default: true
    },
    // 是否显示关闭按钮
    showClose: {
      type: Boolean,
      default: true
    },
    // 关闭时是否销毁对话框
    destroyOnClose: {
      type: Boolean,
      default: true
    },
    // 表单字段配置
    fields: {
      type: Array,
      default: () => []
    },
    // 表单数据
    formData: {
      type: Object,
      default: () => {}
    },
    // 是否自动同步到父组件的formData
    autoSync: {
      type: Boolean,
      default: true
    },
    // 是否在打开时自动聚焦第一个字段
    autoFocus: {
      type: Boolean,
      default: false
    },
    // 表单验证规则
    rules: {
      type: Object,
      default: () => {}
    },
    // 表单列数
    columnCount: {
      type: Number,
      default: 1
    },
    // 标签宽度
    labelWidth: {
      type: [String, Number],
      default: '100px'
    },
    labelPosition: {
      type: String,
      default: 'right'
    },
    // 是否为查看模式
    isView: {
      type: Boolean,
      default: false
    },
    // 确认按钮文本
    confirmButtonText: {
      type: String,
      default: '确认'
    },
    // 取消按钮文本
    cancelButtonText: {
      type: String,
      default: '取消'
    },
    // 确认回调
    onConfirm: {
      type: Function,
      default: null
    },
    // 取消回调
    onCancel: {
      type: Function,
      default: null
    },
    // 关闭回调
    onClose: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      // 内部表单数据，避免直接修改传入的formData
      innerFormData: { ...this.formData },
      // 用于跟踪是否是首次加载
      isFirstLoad: true,
      // 内部可见性状态，避免直接修改prop
      localVisible: this.visible
    }
  },
  watch: {
    // 监听外部formData变化，同步到内部
    formData: {
      handler(newVal) {
        // 避免不必要的更新
        if (!this.isEqual(this.innerFormData, newVal)) {
          this.innerFormData = { ...newVal }
        }
      },
      deep: true
    },
    // 监听外部visible变化，同步到内部
    visible: {
      handler(newVal) {
        this.localVisible = newVal
        if (newVal) {
          // 对话框打开时，重新初始化表单数据
          this.innerFormData = { ...this.formData }
          this.isFirstLoad = false

          this.$nextTick(() => {
            // 强制清除验证状态
            if (this.$refs.formRef) {
              this.$refs.formRef.clearValidate()
            }

            if (this.autoFocus) {
              this.focusFirstField()
            }
          })
        } else {
          // 对话框关闭时重置首次加载标记
          this.isFirstLoad = true
        }
      }
    },
    // 监听内部visible变化，通知外部
    localVisible: {
      handler(newVal) {
        this.$emit('update:visible', newVal)
      }
    }
  },
  methods: {
    // 判断两个对象是否相等
    isEqual(obj1, obj2) {
      if (obj1 === obj2) return true
      if (!obj1 || !obj2) return false
      if (Object.keys(obj1).length !== Object.keys(obj2).length) return false

      for (const key in obj1) {
        if (obj1[key] !== obj2[key]) {
          return false
        }
      }
      return true
    },

    // 根据字段类型获取对应的组件
    getComponentByType(type) {
      return componentMap[type] || 'el-input'
    },

    // 获取组件属性配置
    getComponentProps(field) {
      const props = { ...(field.props || {}) }
      
      // 为日期时间类型添加默认格式
      if (field.type === 'date' || field.type === 'datetime') {
        if (!props.valueFormat) {
          props.valueFormat = 'yyyy-MM-dd HH:mm:ss'
        }
      }
      
      return props
    },

    // 聚焦第一个可编辑字段
    focusFirstField() {
      if (!this.fields || this.fields.length === 0) return

      const firstEditableField = this.fields.find(field => field.editable !== false)
      if (firstEditableField && firstEditableField.prop) {
        this.$nextTick(() => {
          if (!this.$refs.formRef) {
            console.error('formRef is not defined')
            return
          }

          // 尝试多种方式查找输入元素
          let input = this.$refs.formRef.$el.querySelector(`[name="${firstEditableField.prop}"]`)

          // 如果没找到，尝试查找el-form-item__content下的输入元素
          if (!input) {
            const formItem = this.$refs.formRef.$el.querySelector(
              `.el-form-item[data-prop="${firstEditableField.prop}"]`
            )
            if (formItem) {
              input = formItem.querySelector('.el-form-item__content > *')
            }
          }

          // 如果还是没找到，尝试通过prop遍历查找
          if (!input) {
            console.warn(`Cannot find input element for prop: ${firstEditableField.prop}`)
            // 可以在这里添加更多的查找逻辑
          } else {
            input.focus()
          }
        })
      }
    },

    // 处理字段变化
    handleFieldChange(prop, value) {
      this.$emit('fieldChange', prop, value)
      this.syncToParent(prop, value)
    },

    // 同步到父组件的formData
    syncToParent(prop, value) {
      if (this.autoSync) {
        // 创建一个新对象，避免直接修改prop
        const newFormData = { ...this.formData }
        newFormData[prop] = value
        // 通过.sync修饰符同步到父组件
        this.$emit('update:formData', newFormData)
      }
    },

    // 处理确认
    handleConfirm() {
      if (!this.$refs.formRef) {
        this.handleClose()
        this.$emit('confirm', this.innerFormData)
        return
      }

      this.$refs.formRef.validate(async valid => {
        if (valid) {
          // 调用确认回调
          if (this.onConfirm) {
            try {
              const result = await this.onConfirm(this.innerFormData)
              // 如果回调返回false，则不关闭对话框
              if (result !== false) {
                this.handleClose()
              }
            } catch (error) {
              console.error('ProDialog confirm error:', error)
              ElMessage.error('操作失败')
            }
          } else {
            this.handleClose()
          }
          this.$emit('confirm', this.innerFormData)
        }
      })
    },

    // 处理取消
    handleCancel() {
      // 调用取消回调
      if (this.onCancel) {
        this.onCancel()
      }
      this.handleClose()
      this.$emit('cancel')
    },

    // 处理关闭
    handleClose() {
      // 调用关闭回调
      if (this.onClose) {
        try {
          this.onClose()
        } catch (error) {
          console.error('ProDialog close error:', error)
        }
      }
      this.localVisible = false
      // 重置表单状态和数据
      if (this.$refs.formRef) {
        try {
          // 先清空表单数据
          this.clearFormData()
          // 然后重置表单状态
          this.$refs.formRef.resetFields()
          // 强制清除验证状态
          this.$refs.formRef.clearValidate()
        } catch (error) {
          console.error('ProDialog reset form error:', error)
        }
      }
    },

    // 清空表单数据
    clearFormData() {
      if (this.fields && this.fields.length > 0) {
        // 创建一个新的空表单数据对象
        const emptyFormData = {}
        // 遍历所有字段，将其值设置为空
        this.fields.forEach(field => {
          if (field.prop) {
            // 可以根据字段类型设置不同的默认值
            emptyFormData[field.prop] = ''
          }
        })
        this.innerFormData = emptyFormData
        
        // 如果启用了自动同步，也同步到父组件
        if (this.autoSync) {
          this.$emit('update:formData', { ...emptyFormData })
        }
      } else {
        // 如果没有字段配置，直接清空
        this.innerFormData = {}
        if (this.autoSync) {
          this.$emit('update:formData', {})
        }
      }
    },

    /*********ref回调************/
    handleOpen() {
      this.localVisible = true
    },
    // 重置表单
    resetForm() {
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields()
      }
    },

    // 验证表单
    validateForm() {
      return new Promise(resolve => {
        this.$refs.formRef.validate(valid => {
          resolve(valid)
        })
      })
    },

    // 获取表单数据
    getFormData() {
      return this.innerFormData
    },

    // 设置表单数据
    setFormData(data) {
      this.innerFormData = { ...data }
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 15px;
}

/* 调整表单布局 */
.el-form--inline .el-form-item {
  margin-right: 0 !important;
}

/* 确保在小屏幕上也能正常显示 */
@media screen and (max-width: 768px) {
  .el-dialog {
    width: 90% !important;
  }
}
</style>
